import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Provider } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { ConfirmarResetSenhaComponent } from './components/confirmar-reset-senha/confirmar-reset-senha.component';
import { LoginComponent } from './components/login/login.component';
import { NovoCadastroComponent } from './components/novo-cadastro/novo-cadastro.component';
import { ResetSenhaComponent } from './components/reset-senha/reset-senha.component';
import { AUTH_CONFIG, AuthModuleConfig, DEFAULT_AUTH_CONFIG } from './config/auth-config';
import { loginGuard } from './guards/login.guard';

/**
 * Módulo de autenticação reutilizável para aplicações Chronos
 *
 * @example
 * ```typescript
 * @NgModule({
 *   imports: [
 *     AuthModule.forRoot({
 *       theme: {
 *         logoUrl: 'assets/logo.png',
 *         siteUrl: 'https://example.com',
 *         appName: 'My App'
 *       },
 *       features: {
 *         enableRegistration: true,
 *         enablePasswordReset: true
 *       }
 *     }, { provide: IAuthService, useClass: MyAuthService })
 *   ]
 * })
 * export class AppModule {}
 * ```
 */
@NgModule({
  imports: [CommonModule],
  exports: [],
})
export class AuthModule {
  /**
   * Configura o módulo de autenticação com as configurações específicas da aplicação
   *
   * @param config Configuração do módulo incluindo tema, funcionalidades e rotas
   * @param authServiceProvider Provider para implementação do IAuthService
   * @returns ModuleWithProviders configurado
   */
  static forRoot(
    config: AuthModuleConfig,
    authServiceProvider: Provider
  ): ModuleWithProviders<AuthModule> {
    const mergedConfig: AuthModuleConfig = {
      ...DEFAULT_AUTH_CONFIG,
      ...config,
      routes: {
        ...DEFAULT_AUTH_CONFIG.routes,
        ...config.routes,
      },
      features: {
        ...DEFAULT_AUTH_CONFIG.features,
        ...config.features,
      },
    };

    const routes: Routes = [
      {
        path: mergedConfig.routes?.loginRoute || 'login',
        component: LoginComponent,
        canActivate: [loginGuard],
      },
    ];

    // Adiciona rota de reset de senha se habilitado
    if (mergedConfig.features.enablePasswordReset) {
      routes.push(
        {
          path:
            mergedConfig.routes?.requestPasswordResetRoute ||
            'solicitar-reset-senha',
          component: ResetSenhaComponent,
        },
        {
          path:
            mergedConfig.routes?.confirmPasswordResetRoute || 'reset-senha',
          component: ConfirmarResetSenhaComponent,
        }
      );
    }

    // Adiciona rota de cadastro se habilitado
    if (mergedConfig.features.enableRegistration) {
      routes.push({
        path: mergedConfig.routes?.registrationRoute || 'novo-cadastro',
        component: NovoCadastroComponent,
      });
    }

    @NgModule({
      imports: [CommonModule, RouterModule.forChild(routes)],
      exports: [RouterModule],
    })
    class AuthRootModule { }

    return {
      ngModule: AuthRootModule,
      providers: [
        { provide: AUTH_CONFIG, useValue: mergedConfig },
        authServiceProvider,
      ],
    };
  }
}
